import { HttpErrorResponse } from '@angular/common/http';
import { TimeoutError } from 'rxjs';

import { AppError } from './app-error.model';
import { DEFAULT_ERROR_MESSAGES } from './error-message.constants';
import { FieldError } from './field-error.model';

export function mapHttpError(error: HttpErrorResponse | TimeoutError): AppError {
    if (error instanceof TimeoutError || (error as { name?: string }).name === 'TimeoutError') {
        return fromType('timeout');
    }

    if (!(error instanceof HttpErrorResponse)) {
        return fromType('unknown');
    }

    if (error.status === 0) {
        return fromType('network');
    }

    if (error.status === 400) {
        const fieldErrors = extractFieldErrors(error.error);
        return {
            ...fromType(fieldErrors.length > 0 ? 'validation' : 'service'),
            fieldErrors: fieldErrors.length > 0 ? fieldErrors : undefined
        };
    }

    if (error.status === 401) {
        return fromType('authentication');
    }

    if (error.status === 403) {
        return fromType('authorization');
    }

    if (error.status === 404) {
        return fromType('notFound');
    }

    if (error.status === 408 || error.status === 504) {
        return fromType('timeout');
    }

    if (error.status >= 500) {
        return fromType('service');
    }

    return fromType('unknown');
}

function fromType(type: AppError['type']): AppError {
    const fallback = DEFAULT_ERROR_MESSAGES[type];
    return {
        type,
        title: fallback.title,
        message: fallback.message,
        retryable: fallback.retryable
    };
}

function extractFieldErrors(payload: unknown): FieldError[] {
    if (!payload || typeof payload !== 'object') {
        return [];
    }

    const candidate = payload as Record<string, unknown>;
    const errors = candidate.errors || candidate.fieldErrors || candidate.validationErrors;

    if (Array.isArray(errors)) {
        return errors
            .map((item) => mapFieldErrorItem(item))
            .filter((item): item is FieldError => !!item);
    }

    if (errors && typeof errors === 'object') {
        return Object.entries(errors as Record<string, unknown>)
            .map(([field, value]) => ({
                field,
                message: toSafeFieldMessage(value)
            }))
            .filter((item) => item.message.length > 0);
    }

    return [];
}

function mapFieldErrorItem(item: unknown): FieldError | null {
    if (!item || typeof item !== 'object') {
        return null;
    }

    const candidate = item as Record<string, unknown>;
    const field = candidate.field || candidate.name || candidate.property;
    const message = candidate.message || candidate.defaultMessage;

    if (typeof field !== 'string' || typeof message !== 'string') {
        return null;
    }

    return { field, message };
}

function toSafeFieldMessage(value: unknown): string {
    const nextValue = Array.isArray(value) ? value[0] : value;

    if (typeof nextValue === 'string') {
        return nextValue;
    }

    if (typeof nextValue === 'number') {
        return String(nextValue);
    }

    return '';
}
